import {
  handleLoginAction,
  handleLogoutAction,
  handleRegisterAction,
  handleResetPasswordAction,
  handleSendOtpAction,
  handleVerifyOtpAction,
} from "@/actions/auth.action";
import {
  LoginSuccess,
  RegisterSuccess,
  ResetPasswordSuccess,
  SendOtpSuccess,
  VerifyOtpSuccess,
} from "@/types/auth";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useRouter } from "next/navigation";

type UseOptions<T = void> = {
  onSuccess?: (data: T) => void;
  onError?: (message: string, code?: string | number) => void;
};

const invalidateAuthDependentQueries = (
  queryClient: ReturnType<typeof useQueryClient>,
) => {
  void queryClient.invalidateQueries({
    predicate: (query) => query.queryKey[0] !== "my-profile",
  });
};

const useLogin = (options?: UseOptions<LoginSuccess>) => {
  const queryClient = useQueryClient();
  const router = useRouter();

  return useMutation({
    mutationFn: handleLoginAction,
    onSuccess: (res) => {
      if (!res.success) {
        options?.onError?.(res.message || "Login failed");
        return;
      }
      void queryClient.cancelQueries({ queryKey: ["my-profile"] });
      if ("user" in res && res.user) {
        queryClient.setQueryData(["my-profile"], {
          success: true,
          message: "User fetched successfully",
          user: res.user,
        });
      }
      invalidateAuthDependentQueries(queryClient);
      router.refresh();
      options?.onSuccess?.(res);
    },
    onError: () => {
      options?.onError?.("Something went wrong. Please try again.");
    },
  });
};

const useRegister = (options?: UseOptions<RegisterSuccess>) => {
  const queryClient = useQueryClient();
  const router = useRouter();

  return useMutation({
    mutationFn: handleRegisterAction,
    onSuccess: (data) => {
      if (!data.success) {
        options?.onError?.(
          data.message || "Failed to create account",
          data.code,
        );
        return;
      }
      void queryClient.cancelQueries({ queryKey: ["my-profile"] });
      if ("user" in data && data.user) {
        queryClient.setQueryData(["my-profile"], {
          success: true,
          message: "User fetched successfully",
          user: data.user,
        });
      }
      invalidateAuthDependentQueries(queryClient);
      router.refresh();
      options?.onSuccess?.(data);
    },
    onError: () => {
      options?.onError?.("Something went wrong. Please try again.");
    },
  });
};

const useLogout = (options?: UseOptions) => {
  const queryClient = useQueryClient();
  const router = useRouter();

  return useMutation({
    mutationFn: handleLogoutAction,
    onMutate: async () => {
      // 1. Cancel any outgoing queries so they don't overwrite our optimistic update
      await queryClient.cancelQueries({ queryKey: ["my-profile"] });

      // 2. Snapshot the previous user profile cache for rollback
      const previousUserProfile = queryClient.getQueryData(["my-profile"]);

      // 3. Optimistically set profile to null so UI instantly shows AuthAction
      queryClient.setQueryData(["my-profile"], {
        success: false,
        message: "Logged out",
        user: null,
      });

      return { previousUserProfile };
    },
    onError: (_err, _variables, context) => {
      // Rollback to profile avatar if request failed
      if (context?.previousUserProfile) {
        queryClient.setQueryData(["my-profile"], context.previousUserProfile);
      }
      options?.onError?.("Something went wrong. Please try again.");
    },
    onSuccess: (res, _variables, context) => {
      if (!res.success) {
        // Rollback to profile avatar if backend returned failure
        if (context?.previousUserProfile) {
          queryClient.setQueryData(["my-profile"], context.previousUserProfile);
        }
        options?.onError?.(res.message || "Logout failed");
        return;
      }
      options?.onSuccess?.();
    },
    onSettled: (res) => {
      if (res?.success) {
        queryClient.removeQueries({ queryKey: ["my-profile"] });
        invalidateAuthDependentQueries(queryClient);
        router.refresh();
      }
    },
  });
};

const useResetPassword = (options?: UseOptions<ResetPasswordSuccess>) => {
  return useMutation({
    mutationFn: handleResetPasswordAction,
    onSuccess: (response) => {
      if (!response.success) {
        options?.onError?.(response.message || "Failed to reset password");
        return;
      }
      options?.onSuccess?.(response);
    },
    onError: () => {
      options?.onError?.("Something went wrong. Please try again.");
    },
  });
};

const useSendOtp = (options?: UseOptions<SendOtpSuccess>) => {
  return useMutation({
    mutationFn: handleSendOtpAction,
    onSuccess: (response) => {
      if (!response.success) {
        options?.onError?.(response.message || "Failed to send OTP");
        return;
      }
      options?.onSuccess?.(response);
    },
    onError: () => {
      options?.onError?.("Something went wrong. Please try again.");
    },
  });
};

const useVerifyOtp = (options?: UseOptions<VerifyOtpSuccess>) => {
  return useMutation({
    mutationFn: handleVerifyOtpAction,
    onSuccess: (response) => {
      if (!response.success) {
        options?.onError?.(
          response.message || "Incorrect OTP code. Please try again.",
        );
        return;
      }
      options?.onSuccess?.(response);
    },
    onError: () => {
      options?.onError?.("Incorrect or expired OTP code. Please try again.");
    },
  });
};

export {
  useLogin,
  useRegister,
  useLogout,
  useResetPassword,
  useSendOtp,
  useVerifyOtp,
};
